import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, KeyRound, AlertTriangle, CheckCircle2, Copy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';

/**
 * /admin/2fa-setup-obrigatorio
 *
 * Fluxo forçado (FORCE_2FA_ADMIN=true): quando um admin sem 2FA tenta logar,
 * backend retorna {requires_2fa_setup, setup_token} em vez de access_token.
 * Essa página consome o setup_token, mostra o QR code TOTP e só emite o
 * access_token REAL após o admin confirmar um código válido.
 *
 * Sem essa página, os admins novos ficavam com token quebrado ("undefined")
 * ou passavam sem 2FA — o gate era inútil.
 */
export default function Setup2FAObrigatorio() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Recupera setup_token via 3 caminhos, em ordem:
  //  1) location.state (navigate direto do AdminLogin.js)
  //  2) sessionStorage (recarga da página após setup iniciado)
  //  3) hash fragment (redirect do Google OAuth em auth_google_own.py)
  const stateData = location.state || (() => {
    try {
      const raw = sessionStorage.getItem('force2faSetup');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // Parse hash fragment (formato: #setup_token=xxx&email=yyy&role=...)
    if (window.location.hash) {
      const params = new URLSearchParams(window.location.hash.substring(1));
      const st = params.get('setup_token');
      if (st) {
        return {
          setup_token: st,
          email: params.get('email') || '',
          role: params.get('role') || '',
          nome: params.get('nome') || '',
        };
      }
    }
    return null;
  })();

  const [setupToken] = useState(stateData?.setup_token || '');
  const [email] = useState(stateData?.email || '');
  const [nome] = useState(stateData?.nome || '');

  const [step, setStep] = useState('qr'); // qr → code → backup → done
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!setupToken) {
      setError('Sessão de setup inválida. Faça login novamente.');
      setTimeout(() => navigate('/admin/login'), 2000);
      return;
    }
    startSetup();
  }, []);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.post('/auth/2fa/forced-setup/start', { refresh_token: setupToken });
      setQrCode(r.data.qr_code);
      setSecret(r.data.secret);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao iniciar configuração 2FA');
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    if (code.replace(/\s/g, '').length < 6) return;
    setLoading(true);
    setError('');
    try {
      const r = await api.post('/auth/2fa/forced-setup/confirm', {
        setup_token: setupToken,
        code: code.replace(/\s/g, ''),
      });
      const codes = r.headers['x-backup-codes']
        ? r.headers['x-backup-codes'].split(',')
        : [];
      setBackupCodes(codes);
      // Persiste sessão real
      login(r.data.access_token, r.data.user, r.data.refresh_token || null);
      localStorage.setItem('adminToken', r.data.access_token);
      localStorage.setItem('adminUser', JSON.stringify(r.data.user));
      try { sessionStorage.removeItem('force2faSetup'); } catch { /* ignore */ }
      setStep('backup');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Código inválido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const copyBackups = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Habilitar 2FA (obrigatório)</h1>
          <p className="text-purple-200 text-sm">
            {nome ? `Olá, ${nome}. ` : ''}Sua conta admin precisa de autenticação de 2 fatores antes de acessar o painel.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="setup2fa-error">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {step === 'qr' && qrCode && (
            <>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Passo 1:</strong> escaneie o QR code no seu app autenticador (Google Authenticator, Authy, 1Password).
              </p>
              <div className="flex justify-center mb-4">
                <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48 border border-gray-200 rounded-lg" data-testid="setup2fa-qr" />
              </div>
              <details className="mb-4">
                <summary className="text-xs text-gray-500 cursor-pointer">Não consegue escanear? Ver código manual</summary>
                <code className="block mt-2 p-2 bg-gray-100 text-xs break-all rounded" data-testid="setup2fa-secret">{secret}</code>
              </details>

              <p className="text-sm text-gray-700 mb-2">
                <strong>Passo 2:</strong> digite o código de 6 dígitos gerado no app.
              </p>
              <form onSubmit={confirmCode} className="space-y-3">
                <input
                  type="text"
                  required
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full text-center text-2xl tracking-[0.5em] px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono"
                  placeholder="000000"
                  data-testid="setup2fa-code-input"
                />
                <button
                  type="submit"
                  disabled={loading || code.length < 6}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                  data-testid="setup2fa-confirm-btn"
                >
                  {loading ? 'Verificando…' : (<><KeyRound className="w-5 h-5" /> Confirmar e ativar</>)}
                </button>
              </form>
            </>
          )}

          {step === 'backup' && (
            <>
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle2 className="w-6 h-6" />
                <strong className="text-lg">2FA ativado!</strong>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Guarde estes códigos de backup</strong> em um lugar seguro (gerenciador de senhas).
                Cada um pode ser usado UMA vez para entrar caso perca acesso ao app autenticador.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                <ul className="text-sm font-mono grid grid-cols-2 gap-1" data-testid="setup2fa-backup-list">
                  {backupCodes.length === 0
                    ? <li className="col-span-2 text-gray-500 italic">Nenhum código retornado — anote em outra tela.</li>
                    : backupCodes.map((c, i) => <li key={i}>{c}</li>)
                  }
                </ul>
              </div>
              {backupCodes.length > 0 && (
                <button
                  onClick={copyBackups}
                  type="button"
                  className="w-full mb-2 text-sm text-purple-700 hover:text-purple-800 py-2 border border-purple-200 rounded-lg flex items-center justify-center gap-1"
                >
                  <Copy className="w-4 h-4" /> Copiar todos
                </button>
              )}
              <button
                onClick={() => navigate('/admin')}
                type="button"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold"
                data-testid="setup2fa-done-btn"
              >
                Entrar no painel
              </button>
            </>
          )}

          {!qrCode && !error && (
            <div className="text-center text-gray-500 py-6">Carregando QR code…</div>
          )}

          {email && (
            <p className="text-xs text-center text-gray-400 mt-4">
              Conta: {email}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
